// components/vector/ArtboardSizeDialog.tsx
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ARTBOARD_MAX_SIDE, ARTBOARD_MIN_SIDE, ARTBOARD_PRESETS, normalizeArtboardSize, type ArtboardSize } from '#lib/vector-artboards';

interface ArtboardSizeDialogProps {
  title: string;
  confirmLabel: string;
  onPick: (size: ArtboardSize) => void;
  onClose: () => void;
}

const fieldClass = 'h-9 w-full rounded-lg border border-[#2A2A2A] bg-[#141414] px-3 text-white/85 outline-none focus:border-white/25';

// Illustrator's New Document: a size before anything is drawn. Presets cover the usual formats,
// a custom size covers the rest.
const ArtboardSizeDialog: React.FC<ArtboardSizeDialogProps> = ({ title, confirmLabel, onPick, onClose }) => {
  const [presetId, setPresetId] = useState<string>(ARTBOARD_PRESETS[0]!.id);
  const [custom, setCustom] = useState({ width: '1000', height: '1000' });
  const firstRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const customSize = normalizeArtboardSize(custom);
  const chosen = presetId === 'custom' ? customSize : ARTBOARD_PRESETS.find((p) => p.id === presetId)?.size ?? null;

  const confirm = useCallback(() => { if (chosen) onPick(chosen); }, [chosen, onPick]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-[min(92vw,460px)] rounded-[24px] bg-[#212121] p-6 text-white shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="text-white" style={{ fontSize: 17, fontWeight: 600 }}>{title}</h2>
        <p className="mt-1 text-white/50" style={{ fontSize: 13 }}>The artboard keeps this size; every tab can have its own.</p>
        <div className="mt-5 grid grid-cols-3 gap-2">
          {ARTBOARD_PRESETS.map((preset, index) => (
            <button
              key={preset.id}
              ref={index === 0 ? firstRef : undefined}
              type="button"
              onClick={() => setPresetId(preset.id)}
              onDoubleClick={() => onPick(preset.size)}
              aria-pressed={presetId === preset.id}
              className={`rounded-xl border px-3 py-3 text-left transition-colors duration-150 ${presetId === preset.id ? 'border-white/30 bg-white/12' : 'border-[#2A2A2A] bg-[#1B1B1B] hover:bg-white/8'}`}
            >
              <span className="block text-white" style={{ fontSize: 13, fontWeight: 500 }}>{preset.label}</span>
              <span className="block text-white/45" style={{ fontSize: 12 }}>{preset.detail}</span>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setPresetId('custom')}
          aria-pressed={presetId === 'custom'}
          className={`mt-2 w-full rounded-xl border px-3 py-2 text-left transition-colors duration-150 ${presetId === 'custom' ? 'border-white/30 bg-white/12' : 'border-[#2A2A2A] bg-[#1B1B1B] hover:bg-white/8'}`}
          style={{ fontSize: 13 }}
        >
          Custom size
        </button>
        {presetId === 'custom' && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            {(['width', 'height'] as const).map((side) => (
              <label key={side} className="block text-white/60" style={{ fontSize: 12 }}>
                {side === 'width' ? 'Width (px)' : 'Height (px)'}
                <input
                  className={`${fieldClass} mt-1`}
                  style={{ fontSize: 13 }}
                  type="number"
                  min={ARTBOARD_MIN_SIDE}
                  max={ARTBOARD_MAX_SIDE}
                  value={custom[side]}
                  onChange={(event) => setCustom((prev) => ({ ...prev, [side]: event.target.value }))}
                  onKeyDown={(event) => { if (event.key === 'Enter') confirm(); }}
                />
              </label>
            ))}
            {!customSize && <p className="col-span-2 text-[#F87171]" style={{ fontSize: 12 }}>Each side goes from {ARTBOARD_MIN_SIDE} to {ARTBOARD_MAX_SIDE} px.</p>}
          </div>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 rounded-full px-4 text-white/70 hover:bg-white/8" style={{ fontSize: 13 }}>Cancel</button>
          <button type="button" onClick={confirm} disabled={!chosen} className="h-9 rounded-full bg-white px-4 text-black disabled:opacity-40" style={{ fontSize: 13, fontWeight: 500 }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

export default memo(ArtboardSizeDialog);
